
import { onMounted, reactive, ref } from "vue";
import XAtable from "@/components/XATable.vue";
import { sysNoticeQueryList, sysNoticeSave, sysNoticeDel } from "@/api/api";
import XDialog from "@/components/XDialog.vue";
import type { FormInstance } from "ant-design-vue";
import { message } from "ant-design-vue";

interface SearchFormState {
  pageNo: number;
  pageSize: number;
}

interface FormState {
  id: string | null;
  email: string;
  type: string;
  typeArray: number[];
}

export default {
  name: "NoticeList",
  components: {
    XAtable,
    XDialog,
  },
  setup() {
    const searchFormState = reactive<SearchFormState>({
      pageNo: 1,
      pageSize: 10,
    });
    const formState = reactive<FormState>({
      id: null,
      email: "",
      type: "",
      typeArray: [],
    });

    const isAdd = ref(1);
    const dislogVisible = ref(false);
    const addLoading = ref(false);
    const pageNo = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const loading = ref(false);
    const tableData = ref([]);
    const formRef = ref<FormInstance>();
    const searchFormRef = ref<FormInstance>();

    const columns = [
      {
        title: "ID",
        key: "id",
        dataIndex: "id",
      },
      {
        title: "邮箱",
        key: "email",
        dataIndex: "email",
      },
      {
        title: "通知类型",
        key: "type",
        dataIndex: "type",
        slots: {
          customRender: "type",
        },
      },
      {
        title: "操作",
        key: "control",
        dataIndex: "control",
        slots: {
          customRender: "control",
        },
      },
    ];

    // 将逗号分隔的字符串转换为数组
    const getTypeArray = (typeStr: string) => {
      if (!typeStr) return [];
      return typeStr
        .split(",")
        .map((item) => Number(item.trim()))
        .filter((item) => !isNaN(item));
    };

    // 获取类型标签
    const getTypeLabel = (type: number) => {
      const typeMap: Record<number, string> = {
        1: "支付管理",
        2: "工单管理",
        3: "名称待办",
        4: "账户申请",
        5: "资金清零",
      };
      return typeMap[type] || type;
    };

    // 获取类型颜色
    const getTypeColor = (type: number) => {
      const colorMap: Record<number, string> = {
        1: "blue",
        2: "green",
        3: "orange",
        4: "gray",
        5: "red",
      };
      return colorMap[type] || "default";
    };

    const search = () => {
      searchFormState.pageNo = 1;
      getData();
    };

    const resetForm = () => {
      searchFormState.pageNo = 1;
      searchFormRef.value?.resetFields();
      getData();
    };

    const getData = () => {
      loading.value = true;
      sysNoticeQueryList(searchFormState)
        .then((res) => {
          loading.value = false;
          if (res.code === "200") {
            total.value = res.data.total;
            tableData.value = res.data.records;
          }
        })
        .catch(() => {
          loading.value = false;
        });
    };

    const pageNoChange = (val: number) => {
      searchFormState.pageNo = val;
      pageNo.value = val;
      getData();
    };

    const onShowSizeChange = (current: number, size: number) => {
      pageSize.value = size;
      searchFormState.pageSize = size;
      getData();
    };

    const addClick = () => {
      isAdd.value = 1;
      dislogVisible.value = true;
      formState.id = null;
      formState.email = "";
      formState.type = "";
      formState.typeArray = [];
    };

    const editClick = (row: any) => {
      isAdd.value = 0;
      formState.id = row.id;
      formState.email = row.email;
      formState.type = row.type;
      // 将逗号分隔的字符串转换为数组
      formState.typeArray = getTypeArray(row.type);
      dislogVisible.value = true;
    };

    const confirm = async () => {
      try {
        // 先验证类型数组
        if (formState.typeArray.length === 0) {
          message.error("请至少选择一个通知类型！");
          return;
        }

        // 验证表单字段
        await formRef.value?.validateFields();

        addLoading.value = true;

        // 将数组转换为逗号分隔的字符串
        const typeStr = formState.typeArray.sort().join(",");

        const submitData: any = {
          email: formState.email,
          type: typeStr,
        };

        // 只有编辑时才传递id
        if (isAdd.value === 0 && formState.id) {
          submitData.id = formState.id;
        }

        sysNoticeSave(submitData)
          .then((res) => {
            addLoading.value = false;
            if (res.code === "200") {
              message.success(isAdd.value === 1 ? "添加成功！" : "修改成功！");
              dislogVisible.value = false;
              getData();
            } else {
              message.error(res.message || "操作失败！");
            }
          })
          .catch((error) => {
            addLoading.value = false;
            console.error("保存失败:", error);
            message.error("操作失败！");
          });
      } catch (errorInfo) {
        console.log("表单验证失败:", errorInfo);
        // 表单验证失败时，validateFields 会自动显示错误信息
      }
    };

    const delClick = (row: any) => {
      sysNoticeDel({ id: row.id })
        .then((res) => {
          if (res.code === "200") {
            message.success("删除成功！");
            getData();
          } else {
            message.error(res.message || "删除失败！");
          }
        })
        .catch(() => {
          message.error("删除失败！");
        });
    };

    onMounted(() => {
      getData();
    });

    return {
      userList: [],
      isAdd,
      formRef,
      dislogVisible,
      addClick,
      confirm,
      searchFormState,
      searchFormRef,
      formState,
      addLoading,
      columns,
      tableData,
      pageNo,
      total,
      loading,
      search,
      resetForm,
      pageNoChange,
      onShowSizeChange,
      editClick,
      delClick,
      getTypeArray,
      getTypeLabel,
      getTypeColor,
    };
  },
};
